import React from 'react';
import { AGENCY, whatsappLink } from '@/lib/site';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

const WhatsAppGlyph = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    className={cn('h-4 w-4', className)}
  >
    <path d="M17.47 14.38c-.29-.15-1.72-.85-1.99-.95-.27-.1-.46-.15-.66.15-.19.29-.76.95-.93 1.14-.17.19-.34.21-.63.07-.29-.15-1.1-.41-2.1-1.3-.78-.7-1.3-1.56-1.45-1.85-.15-.29-.02-.45.12-.6.13-.13.29-.34.44-.51.14-.17.19-.29.29-.48.1-.19.05-.36-.02-.51-.07-.15-.66-1.59-.9-2.17-.24-.57-.48-.5-.66-.5h-.56c-.19 0-.5.07-.76.36-.26.29-1 .97-1 2.37 0 1.4 1.02 2.75 1.16 2.94.15.19 2 3.17 4.86 4.33 2.38.96 2.86.77 3.38.72.52-.05 1.67-.68 1.9-1.34.24-.66.24-1.22.17-1.34-.07-.12-.26-.19-.55-.34zM12.01 2.4c-5.3 0-9.6 4.3-9.6 9.6 0 1.7.44 3.29 1.22 4.68L2.4 21.6l5.05-1.19a9.55 9.55 0 0 0 4.56 1.16c5.3 0 9.6-4.3 9.6-9.6s-4.3-9.57-9.6-9.57zm0 17.53c-1.5 0-2.9-.42-4.08-1.14l-.29-.17-3.02.71.72-2.94-.19-.31a7.9 7.9 0 0 1-1.21-4.21c0-4.39 3.58-7.96 7.97-7.96 4.39 0 7.96 3.57 7.96 7.96 0 4.39-3.57 8.06-7.86 8.06z" />
  </svg>
);

/** Localised WhatsApp deep link. */
export const useWhatsAppHref = () => {
  const { t } = useI18n();
  return whatsappLink(t.whatsappMessage);
};

/**
 * WhatsApp CTA. variant: solid | outline | ghost | icon
 * `label` defaults to the localised short label; pass `long` for the long one.
 */
export const WhatsAppButton = ({
  variant = 'outline',
  label,
  long = false,
  className,
  testId = 'whatsapp-link',
  size = 'md',
}) => {
  const { t } = useI18n();
  const href = whatsappLink(t.whatsappMessage);
  const text = label ?? (long ? t.common.whatsappLong : t.common.whatsapp);

  const sizes = {
    sm: 'h-9 px-3.5 text-[0.78rem]',
    md: 'h-11 px-5 text-[0.82rem]',
    lg: 'h-12 px-6 text-[0.86rem]',
  };

  const variants = {
    solid:
      'bg-[color:var(--rmh-ink)] text-[color:var(--rmh-canvas)] hover:bg-[color:var(--rmh-ink-2)] border border-transparent',
    outline:
      'bg-[color:var(--rmh-surface)] text-[color:var(--rmh-ink)] border border-[color:var(--rmh-border-strong)] hover:border-[color:var(--rmh-ink)]',
    ghost:
      'bg-transparent text-[color:var(--rmh-ink)] border border-transparent hover:bg-[color:var(--rmh-surface-2)]',
  };

  if (variant === 'icon') {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t.common.whatsappLong}
        data-testid={testId}
        className={cn(
          'inline-flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--rmh-border-strong)] bg-[color:var(--rmh-surface)] text-[color:var(--rmh-ink)] transition-colors duration-300 hover:border-[color:var(--rmh-ink)]',
          className,
        )}
      >
        <WhatsAppGlyph />
      </a>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-[12px] font-medium tracking-[0.02em] transition-colors duration-300',
        sizes[size],
        variants[variant],
        className,
      )}
    >
      <WhatsAppGlyph />
      {text}
    </a>
  );
};

export { WhatsAppGlyph, AGENCY };
export default WhatsAppButton;
